// src/features/records/stores/useFilterStore.js
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

/**
 * Filter scope type definition
 * @typedef {Object} FilterScope
 * @property {'users' | 'group'} mode - Filter mode
 * @property {string[]} [userIds] - Selected user IDs (when mode is 'users')
 * @property {string|null} [groupId] - Selected group ID (when mode is 'group')
 */

// Constants
const STORAGE_KEY_BASE = 'affin_records_filter';
const STORAGE_VERSION = 1;

/**
 * Check if running in browser environment
 * @returns {boolean} True if in browser
 */
const isBrowser = () => typeof window !== 'undefined';

/**
 * Get storage key for a specific user
 * @param {string} userId - User ID
 * @returns {string} User-specific storage key
 */
const getStorageKey = (userId) => {
  if (!userId) return `${STORAGE_KEY_BASE}:anon`;
  return `${STORAGE_KEY_BASE}:${userId}`;
};

/**
 * Parse filter scope from URL parameters
 * @param {string} selfId - Current user ID
 * @returns {FilterScope|null} Parsed scope or null
 */
function parseUrlScope(selfId) {
  if (!isBrowser()) return null;
  
  const url = new URL(window.location.href);
  const scopeParam = url.searchParams.get('scope');
  
  if (scopeParam === 'group') {
    const groupId = url.searchParams.get('gid');
    // Only accept valid group ID
    return groupId ? { mode: 'group', groupId } : null;
  }
  
  if (scopeParam === 'users') {
    const uidsParam = url.searchParams.get('uids');
    const ids = uidsParam 
      ? uidsParam.split(',')
          .map(s => s.trim())
          .filter(Boolean)
      : [selfId];
    // Dedupe and ensure we have valid IDs
    const uniqueIds = Array.from(new Set(ids.length ? ids : [selfId]));
    return { mode: 'users', userIds: uniqueIds };
  }
  
  return null;
}

/**
 * Load filter scope from storage
 * @param {string} selfId - Current user ID
 * @returns {FilterScope|null} Stored scope or null
 */
function loadStoredScope(selfId) {
  if (!isBrowser() || !selfId) return null;
  
  try {
    const storageKey = getStorageKey(selfId);
    const stored = localStorage.getItem(storageKey);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored);
    if (parsed.version !== STORAGE_VERSION) return null;
    
    return parsed.scope;
  } catch (error) {
    console.error('Error loading filter scope from storage:', error);
    return null;
  }
}

/**
 * Save filter scope to storage
 * @param {string} selfId - Current user ID
 * @param {FilterScope} scope - Scope to save
 */
function saveScope(selfId, scope) {
  if (!isBrowser() || !selfId) return;
  
  try {
    const storageKey = getStorageKey(selfId);
    localStorage.setItem(storageKey, JSON.stringify({
      version: STORAGE_VERSION,
      scope
    }));
  } catch (error) {
    console.error('Error saving filter scope to storage:', error);
  }
}

/**
 * Update URL with current scope
 * @param {FilterScope} scope - Current scope
 */
function updateUrl(scope) {
  if (!isBrowser()) return;
  
  const url = new URL(window.location.href);
  
  // Clear existing params
  url.searchParams.delete('scope');
  url.searchParams.delete('uids');
  url.searchParams.delete('gid');
  
  if (scope.mode === 'users' && scope.userIds?.length) {
    url.searchParams.set('scope', 'users');
    // Dedupe before saving to URL
    const uniqueIds = Array.from(new Set(scope.userIds));
    url.searchParams.set('uids', uniqueIds.join(','));
  } else if (scope.mode === 'group' && scope.groupId) {
    url.searchParams.set('scope', 'group');
    url.searchParams.set('gid', scope.groupId);
  }
  
  // Use replaceState to avoid polluting history
  window.history.replaceState({}, '', url.toString());
}

/**
 * Initialize filter scope with user ID
 * @param {string|undefined} selfId - Current user ID
 * @returns {FilterScope} Initial scope
 */
function initializeScope(selfId) {
  if (!selfId) return { mode: 'users', userIds: [] };
  
  // Try URL first, then storage, then default
  const urlScope = parseUrlScope(selfId);
  if (urlScope) return urlScope;
  
  const storedScope = loadStoredScope(selfId);
  if (storedScope) return storedScope;
  
  return { mode: 'users', userIds: [selfId] };
}

/**
 * Zustand store for filter state management
 * Replaces the previous React Context implementation
 */
const useFilterStore = create(
  subscribeWithSelector((set, get) => ({
    // State
    scope: { mode: 'users', userIds: [] },
    selfId: null,
    initialized: false,
    
    // Actions
    /**
     * Initialize the store with user ID (idempotent)
     * @param {string} userId - Current user ID
     */
    initialize: (userId) => {
      const { initialized, selfId } = get();
      
      // Already initialized with same user, skip
      if (initialized && selfId === userId) return;
      
      const scope = initializeScope(userId);
      set({ 
        selfId: userId,
        scope,
        initialized: true
      });
      
      // Save and update URL
      saveScope(userId, scope);
      updateUrl(scope);
    },
    
    /**
     * Set the filter scope with normalization
     * @param {FilterScope} newScope - New filter scope
     */
    setScope: (newScope) => {
      // Normalize the scope
      const normalized = newScope.mode === 'users'
        ? {
            mode: 'users',
            userIds: Array.from(new Set(newScope.userIds || []))
          }
        : {
            mode: 'group',
            groupId: newScope.groupId || null
          };
      
      set({ scope: normalized });
      
      // Persist changes
      const { selfId } = get();
      if (selfId) {
        saveScope(selfId, normalized);
        updateUrl(normalized);
      }
    },
    
    /**
     * Reset to default scope (current user only)
     */
    resetToDefault: () => {
      const { selfId } = get();
      if (!selfId) return;
      
      const defaultScope = { mode: 'users', userIds: [selfId] };
      set({ scope: defaultScope });
      
      // Persist changes
      saveScope(selfId, defaultScope);
      updateUrl(defaultScope);
    },
    
    /**
     * Clear all filters (alias for resetToDefault)
     */
    clearFilters: () => {
      get().resetToDefault();
    },
    
    /**
     * Reset store to initial state (for logout)
     */
    reset: () => {
      set({
        selfId: null,
        scope: { mode: 'users', userIds: [] },
        initialized: false
      });
    }
    
    // Note: isFilterActive removed - compute in components using selectors instead
  }))
);

export default useFilterStore;